<template>
  <div
    class="d-flex flex-wrap gap-1 justify-content-start"
    :class="statusClasses"
  >
    <span
      v-if="isDenied && data.isLoading"
      class="visually-hidden"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      Loading denied visitor options, please wait.
    </span>
    <button
      v-if="isDenied"
      type="button"
      class="d-flex flex-wrap justify-content-center align-content-center gap-2 visitor-status-pill visitor-status-pill-action"
      :class="pillClasses"
      :disabled="data.isLoading"
      :aria-busy="data.isLoading ? 'true' : undefined"
      :aria-label="deniedStatusActionLabel"
      @click.stop="onStatusClick"
    >
      <i
        class="visitor-status-icon"
        :class="iconStyleClasses"
        aria-hidden="true"
      ></i>
      <span class="mt-3px text-capitalize">
        {{ data.isLoading ? "Loading..." : visitor.visit_status }}
      </span>
    </button>
    <div
      v-else
      class="d-flex flex-wrap justify-content-center align-content-center gap-2 visitor-status-pill"
      :class="pillClasses"
    >
      <i
        class="visitor-status-icon"
        :class="iconStyleClasses"
        aria-hidden="true"
      ></i>
      <span class="mt-3px text-capitalize">
        {{ data.isLoading ? "Loading..." : visitor.visit_status }}
      </span>
    </div>

    <template v-if="showInfoIcon">
      <span
        v-if="isBadgePrintingFailed"
        role="img"
        :title="statusTooltip"
        :aria-label="statusTooltip"
        class="m-auto visitor-status-info-icon"
      >
        <i :class="badgePrintingFailedIconClasses" aria-hidden="true"></i>
      </span>

      <VMenu
        v-else
        ref="statusInfoMenuRef"
        v-model:shown="infoMenuShown"
        class="d-flex justify-content-center align-items-center flex-grow-1"
        :triggers="['click']"
        :popper-triggers="['click']"
        :hide-triggers="hideStatusInfoTriggers"
      >
        <button
          type="button"
          class="visitor-status-info-button"
          :title="statusTooltip || undefined"
          :aria-label="statusTooltip || 'Visit exception details'"
          :aria-expanded="infoMenuShown ? 'true' : 'false'"
          @keydown.esc.stop.prevent="hideStatusInfoTooltip"
        >
          <i :class="tooltipIconClasses" aria-hidden="true"></i>
        </button>

        <template #popper>
          <div tabindex="-1" @keydown.esc.stop.prevent="hideStatusInfoTooltip">
            <VisitorEntryLog
              :visitor="visitor"
              @close="hideStatusInfoTooltip"
            />
          </div>
        </template>
      </VMenu>
    </template>
  </div>
</template>

<script>
import useDeniedVisitorFlow from "@/composables/visitor/useDeniedVisitorFlow"
import visitorConstants from "@/constants/visitorConstants"
import VisitorEntryLog from "@/v3components/Datatables/Visitor/CheckIns/VisitorEntryLog.vue"
import { announce } from "@/composables/useLiveAnnouncer"
import { computed, inject, ref, watch } from "vue"

export default {
  name: "VisitorStatusColumn",
  components: {
    VisitorEntryLog
  },
  props: {
    visitor: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props) {
    const modal = inject("modal")
    const infoMenuShown = ref(false)
    const { data, startProcess } = useDeniedVisitorFlow(props.visitor, modal)
    const statusInfoMenuRef = ref(null)

    const isDenied = computed(
      () => props?.visitor?.visit_status?.toLowerCase() === "denied"
    )

    const statusClasses = computed(() => {
      const classMap = {
        denied: "visitor-status-red",
        "signed in": "visitor-status-green",
        "signed out": "visitor-status-blue"
      }
      return (
        classMap?.[props?.visitor?.visit_status?.toLowerCase()] ||
        "visitor-status-default"
      )
    })

    const iconStyleClasses = computed(() => {
      const visitStatus = props?.visitor?.visit_status?.toLowerCase()
      return {
        "signed-in ri-check-line": visitStatus === "signed in",
        "signed-out ri-check-line": visitStatus === "signed out",
        "denied ri-close-line": visitStatus === "denied"
      }
    })

    const showInfoIcon = computed(() => {
      return (
        props?.visitor?.signin_exception_message?.toString()?.trim()?.length >
          0 || deniedLogMessages?.value?.length > 0
      )
    })

    const isBadgePrintingFailed = computed(
      () =>
        props?.visitor?.signin_exception_message
          ?.toString()
          ?.trim()
          ?.toLowerCase() === "app_badge_printing_failed"
    )

    const deniedLogMessages = computed(() => {
      const parsedLog = JSON.parse(props?.visitor?.denied_log) || {}
      if (Object.keys(parsedLog).length === 0) return ""

      const messages = []
      const logKeys = visitorConstants.DENIED_LOG_MESSAGE_KEYS
      const flowKeys = visitorConstants.DENIED_FLOW_KEYS

      Object.keys(logKeys).forEach((key) => {
        const logMessage = parsedLog?.[logKeys?.[key] || ""]?.toString()?.trim()
        if (logMessage?.length > 0) {
          messages.push(`${flowKeys[key]}: ${logMessage}`)
        }
      })

      return messages.join(" | ") || ""
    })

    const statusTooltip = computed(() => {
      if (isBadgePrintingFailed.value) return "Badge printing failed"
      return (
        deniedLogMessages.value ||
        props?.visitor?.signin_exception_message ||
        ""
      )
    })

    const hideStatusInfoTooltip = () => {
      statusInfoMenuRef.value?.hide?.()
    }

    const hideStatusInfoTriggers = (triggers) => {
      const fallbackTriggers = Array.isArray(triggers) ? triggers : []
      return [...fallbackTriggers, "click"]
    }

    /* The inline "Loading..." live region is inserted already populated, so it isn't
       reliably announced; mirror it to the persistent global live region (Deque 2190064). */
    watch(
      () => data.isLoading,
      (loading) => {
        if (loading) {
          announce("Loading denied visitor options, please wait.", {
            assertive: true
          })
        }
      }
    )

    const onStatusClick = async () => {
      if (data.isLoading || !isDenied.value) return
      await startProcess()
    }

    const deniedStatusActionLabel = computed(() => {
      const v = props?.visitor
      const first = (v?.first_name || v?.visitor_first_name || "").trim()
      const last = (v?.last_name || v?.visitor_last_name || "").trim()
      const name = [first, last].filter(Boolean).join(" ").trim()
      const who = name ? ` for ${name}` : ""
      return `Open denied visitor actions${who}`
    })

    const pillClasses = computed(() => ({
      "cursor-pointer bg-red-500": isDenied.value
    }))

    const tooltipIconClasses = computed(() => ({
      "ri-information-line": !isBadgePrintingFailed.value,
      "ri-printer-line visitor-status-red": isBadgePrintingFailed.value
    }))

    const badgePrintingFailedIconClasses = computed(() => ({
      "ri-printer-line visitor-status-red": isBadgePrintingFailed.value
    }))

    return {
      infoMenuShown,
      data,
      onStatusClick,
      isDenied,
      deniedStatusActionLabel,
      iconStyleClasses,
      statusClasses,
      statusTooltip,
      showInfoIcon,
      isBadgePrintingFailed,
      pillClasses,
      tooltipIconClasses,
      badgePrintingFailedIconClasses,
      statusInfoMenuRef,
      hideStatusInfoTooltip,
      hideStatusInfoTriggers
    }
  }
}
</script>

<style scoped>
.mt-3px {
  margin-top: 3px;
}

.cursor-pointer {
  cursor: pointer;
}

.visitor-status-info-button {
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:focus-visible {
    outline: 2px solid var(--v3-focus-outline, #2b6cb0);
    outline-offset: 2px;
    border-radius: 4px;
  }
}

.visitor-status-info-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

button.visitor-status-pill-action {
  margin: 0;
  font: inherit;
  text-align: center;
  appearance: none;
  -webkit-appearance: none;
}
</style>
